import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 ek-scanline">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm border p-8" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
          <span className="font-head font-bold tracking-tight">EvidenceKeeper</span>
        </div>

        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-4" style={{ color: "var(--ek-secure)" }} />
            <p className="mb-6" style={{ color: "var(--ek-text-secondary)" }}>Your password has been reset.</p>
            <Button data-testid="reset-goto-login" onClick={() => navigate("/")} className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>Continue to sign in</Button>
          </div>
        ) : !token ? (
          <p className="text-sm" style={{ color: "var(--ek-error)" }}>Missing reset token. Please use the link from your email.</p>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div className="ek-stamp inline-block mb-1">SET NEW PASSWORD</div>
            <div className="space-y-2">
              <Label htmlFor="np">New password</Label>
              <div className="relative">
                <Input id="np" data-testid="reset-password-input" type={show ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-transparent pr-10" placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 transition-colors" data-testid="reset-toggle-visibility">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm" style={{ color: "var(--ek-error)" }}>{error}</p>}
            <Button type="submit" disabled={busy} data-testid="reset-submit-btn" className="w-full rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {busy ? "UPDATING..." : "Reset password"}
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
